import { COURSE_CONTENT_TYPE } from '../../domain/domainCodes.js'
import { CERTIFICATE_PRINT_LANGUAGE, CERTIFICATE_VALIDITY_VISIBILITY, COURSE_EXCEPTION_TYPE, ENTITY_ID } from '../../domain/domainCodes.js'
import { ApiError, api } from '../../lib/api'
import { isEnglish } from '../../lib/i18n'
import { fileToBase64 } from '../../lib/file'
import { hijriToIso } from '../../lib/hijri'
import { strings as S } from './strings'

/**
 * Learning programmes — `Features/Admin/LearningProgramsManagement/Endpoint.cs`.
 *
 *   getProgramsPage   -> GET  /api/admin/programs-management/ | /{id}
 *   listPrograms      -> POST /ProgramsManagement/GetPrograms
 *   saveProgram       -> POST /ProgramsManagement/SaveProgramBasicInfos
 *                     +  POST /ProgramsManagement/SaveProgramGroups
 *                     +  POST /ProgramsManagement/SaveProgramCertificate
 *   deleteProgram     -> POST /ProgramsManagement/DeleteProgram
 *   cloneProgram      -> POST /ProgramsManagement/CloneProgram
 *   setProgramActive  -> POST /ProgramsManagement/SetActivationStatus
 *   saveProgramsOrder -> POST /ProgramsManagement/SaveProgramsOrder
 *   listProgramCourses-> POST /ProgramsManagement/GetProgramCoursesDetails
 *   saveCourse        -> POST /ProgramsManagement/SaveCourse
 *   deleteCourse      -> POST /ProgramsManagement/DeleteCourse
 *   attachments       -> POST /ProgramsManagement/{GetCourseAttachments,AddAttachment,
 *                             UpdateAttachment,RemoveCourseAttachments}
 *   exceptions        -> POST /ProgramsManagement/{GetExceptionsUsers,
 *                             SetUsersFromExceptions,RemoveUsersFromExceptions}
 *
 * THREE THINGS THAT SHAPE THIS MODULE.
 *
 * 1. SAVING A PROGRAMME IS THREE CALLS. The basics, the groups it is assigned to and its
 *    certificate are three endpoints with three request shapes. The screen shows one form,
 *    so `saveProgram` runs them in order and stops at the first refusal — which means a
 *    failure can leave the basics saved and the groups not. Named in `API_FINDINGS.md` §32.
 *
 * 2. FILES ARE BASE64 INSIDE THE JSON, not multipart — `BannerBase64`, `FileArabic`. The
 *    opposite of the categories slice next door, which matches parts by filename.
 *
 * 3. THE LANGUAGE COMES FROM `Accept-Language`, not a parameter. Several handlers read the
 *    header to decide which name to return, and `api.js` already sends it.
 */

const BASE = '/api/admin/programs-management'

export async function importScormBundle(programId, file) {
  const form = new FormData()
  form.append('programId', String(programId))
  form.append('archive', file)
  return api.post('/ProgramsManagement/ImportScormBundle', form)
}

export async function exportScormBundle(courseId) {
  const response = await fetch(`/ProgramsManagement/ExportScormBundle/${courseId}`, {
    credentials: 'same-origin', headers: { Accept: 'application/zip' }, redirect: 'manual',
  })
  if (!response.ok || !response.headers.get('Content-Type')?.includes('application/zip')) {
    const detail = await response.json().catch(() => null)
    throw new ApiError(detail?.message || S.actionFailed)
  }
  const url = URL.createObjectURL(await response.blob())
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `rawam-scorm-${courseId}.zip`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
}

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/** `yyyy-MM-dd` sorts as text, so no parsing is needed to find the ends of a range. */
const dates = (values) => values.filter((value) => value)
const earliest = (values) => dates(values).sort()[0] ?? ''
const latest = (values) => dates(values).sort().at(-1) ?? ''

/**
 * Everything the screen needs before its first row, in one call.
 *
 * @param {number} [programId] selects a programme, as `/ProgramsManagement/{id}` did.
 */
export async function getProgramsPage(programId, signal) {
  const dto = (await api.get(programId ? `${BASE}/${programId}` : `${BASE}/`, { signal })) ?? {}

  return {
    programs: (dto.Programs ?? []).map(toProgramRow),
    /* The same programmes in the order they are shown in, which `SaveProgramsOrder` writes
       back. Kept apart because the two lists genuinely differ. */
    ordered: (dto.OrderedPrograms ?? []).map((program) => program.Id),
    selectedProgramId: dto.SelectedProgramId ?? null,
    /* `NameArabic` / `NameEnglish` — the names `GroupDto` and `CertificateTemplateDto`
       actually declare. This read `ArabicName` / `EnglishName`, which are on neither, so
       every one of these lists rendered with an EMPTY label: a department picker of blank
       checkboxes, a certificate dropdown of blank rows. The values were right, so the
       controls worked and simply could not be read. */
    groups: (dto.Groups ?? []).map((group) => ({
      value: group.GroupId ?? group.Id,
      label: label(group.NameArabic, group.NameEnglish),
      /* Departments come back ordered by type; the picker groups them under it. */
      groupTypeId: group.GroupTypeId ?? 0,
    })),
    certificates: (dto.Certificates ?? []).map((certificate) => ({
      value: certificate.Id,
      label: label(certificate.NameArabic, certificate.NameEnglish),
    })),
    /* ALREADY LOCALIZED. `CourseLevelDto` and `DifficultyLevelDto` carry one `Name`, chosen
       server-side from the request's language — so there is no pair to pick between here,
       and asking for one returned nothing. */
    difficulties: (dto.DifficultyLevels ?? []).map((level) => ({
      value: level.Id ?? level.DifficultyId,
      label: level.Name ?? '',
    })),
    /* `CategoryId`, which is what `CategoryDto` actually declares — reading `Id` gave every
       topic an undefined value, so the course form's topic picker could be ticked and still
       post nothing. `Id` stays as a fallback in case the shape is ever normalised. */
    categories: (dto.Categories ?? []).map((category) => ({
      value: category.CategoryId ?? category.Id,
      label: label(category.ArabicName, category.EnglishName),
      /* Both names, for quick create: each course it makes is named after its topic. */
      arabicName: category.ArabicName ?? '',
      englishName: category.EnglishName ?? '',
      examQuestions: category.ExamQuestionsCount ?? null,
      hasVideoContent: Boolean(category.HasVideoContent),
      usedByScormCourse: Boolean(category.UsedByScormCourse),
    })),
    levels: (dto.Levels ?? []).map((level) => ({
      value: level.Id,
      label: level.Name ?? '',
    })),
    notifications: {
      onStart: Boolean(dto.StartNotification),
      onEnd: Boolean(dto.EndNotification),
    },
    /** Counts, not outcome shares. See `listPrograms`. */
    dashboard: dto.MainDashboard
      ? {
          totalPrograms: dto.MainDashboard.TotalPrograms ?? 0,
          activePrograms: dto.MainDashboard.ActivePrograms ?? 0,
          totalCourses: dto.MainDashboard.TotalCourses ?? 0,
          totalUsers: dto.MainDashboard.TotalUsers ?? 0,
        }
      : null,
  }
}

function toProgramRow(dto) {
  return {
    id: dto.Id,
    /* DISPLAY fields, for the table — whichever language the panel is in. */
    name: label(dto.NameArabic, dto.NameEnglish),
    nameEn: dto.NameEnglish ?? '',
    description: label(dto.DescriptionArabic, dto.DescriptionEnglish),
    descriptionEn: dto.DescriptionEnglish ?? '',
    /* EDIT fields, under the names the form and `SaveProgramBasicInfos` both use. The two
       sets are not interchangeable: `name` is whichever language is showing, so seeding an
       edit form from it would write the English name into the Arabic column. */
    arabicName: dto.NameArabic ?? '',
    englishName: dto.NameEnglish ?? '',
    arabicDescription: dto.DescriptionArabic ?? '',
    englishDescription: dto.DescriptionEnglish ?? '',
    pastCoursesView: dto.PastCoursesView ?? '',
    futureCoursesView: dto.FutureCoursesView ?? '',
    allowAddingCoursesAfterExamination: Boolean(dto.AllowAddingCoursesAfterExamination),
    createdOn: dto.CreatedOn ?? '',
    isActive: Boolean(dto.IsActive),
    /* Already localized server-side; shown as received rather than re-derived from the
       boolean beside it (W6). */
    statusText: dto.IsActiveText ?? '',
    courses: (dto.Courses ?? []).map((course) => ({
      id: course.Id,
      name: label(course.NameArabic, course.NameEnglish),
      startDate: course.Start ?? course.StartDate ?? '',
      endDate: course.End ?? course.EndDate ?? '',
      // The detail page shows which courses carry an exam.
      hasExam: Boolean(course.HasExam),
      quizEnable: Boolean(course.QuizEnable),
      contentType: course.ContentType ?? 0,
      levelId: course.LevelId ?? null,
    })),
    groupIds: (dto.Groups ?? []).map((group) => group.Id ?? group.GroupId),
    /* The table shows counts, not the lists. */
    coursesCount: (dto.Courses ?? []).length,
    groupsCount: (dto.Groups ?? []).length,
    /* The programme's span. `MinCourseDate` / `MaxCourseDate` are the server's own, and
       authoritative — the fallback derives the same thing from the courses in the payload
       for a programme that has not had them computed. */
    startDate:
      dto.MinCourseDate ??
      earliest((dto.Courses ?? []).map((course) => course.Start ?? course.StartDate)),
    endDate:
      dto.MaxCourseDate ??
      latest((dto.Courses ?? []).map((course) => course.End ?? course.EndDate)),
    /* THE SURVEY'S STATE, which the payload has carried all along —
       `HasAnySurveyQuestion` and `IsSurveyEnabled` on `ProgramDto`. The card showed a red
       cross unconditionally because nothing read them. The legacy ticks its own tile on
       `HasAnySurveyQuestion && IsSurveyEnabled`: a survey nobody can answer is not a survey
       this programme has. */
    hasSurvey: Boolean(dto.HasAnySurveyQuestion),
    isSurveyEnabled: Boolean(dto.IsSurveyEnabled),
    certificateId: dto.CertificateId ?? null,
    certificate: {
      printLanguage: dto.CertificatePrintLanguage ?? '',
      userPrint: Boolean(dto.EnableCertificateUserPrint),
      grade: Boolean(dto.EnableCertificateGrade),
      dateFormat: dto.CertificateDateFormat ?? 0,
      dateConfig: dto.CertificateDateConfig ?? 0,
      personalEmail: Boolean(dto.EnableCertificatePersonalEmailSend),
      emailSend: Boolean(dto.EnableCertificateEmailSend),
      departmentName: Boolean(dto.EnableCertificateDepartmentName),
      gradeA: dto.GradeA ?? 0,
      gradeB: dto.GradeB ?? 0,
      gradeC: dto.GradeC ?? 0,
    },
    assignToNewIntegratedGroups: Boolean(dto.AssignToNewIntegratedGroups),
  }
}

/**
 * The programmes, and the four counts above them.
 *
 * NOT the outcome shares the screen used to draw. `DashboardDto` carries
 * `TotalPrograms / ActivePrograms / TotalCourses / TotalUsers` — counts, not a pass/fail
 * breakdown — and no endpoint in this slice produces one. The dials are hidden rather than
 * filled with something invented; registered in `COSMETIC_UI.md`.
 */
export async function listPrograms(signal) {
  const page = await getProgramsPage(undefined, signal)
  return {
    rows: page.programs,
    total: page.programs.length,
    dashboard: page.dashboard,
    /* The saved running order (`OrderedPrograms`, active programmes only). Dropped here
       until now, so the ordering screen opened on the table's order every time and a
       saved arrangement looked like it had not been saved at all. */
    ordered: page.ordered,
  }
}

/**
 * The course levels the course form offers, from the page payload.
 *
 * Its own endpoint does not exist — the levels ride along with everything else the screen
 * needs, which is one call instead of two.
 */
export async function listCourseLevels(signal) {
  return (await getProgramsPage(undefined, signal)).levels
}

/**
 * The three-step wizard: a programme, then a course per chosen topic.
 *
 * COMPOSED, NOT A CALL. There is no quick-create endpoint in this project — the legacy
 * fanned one form out server-side and nothing here does. So it is fanned out from the
 * browser using the two endpoints that do exist, which means it is NOT ATOMIC: a course
 * that fails leaves the programme and the courses before it in place. The count of what
 * was created comes back so the screen can say so rather than claim a clean run.
 */
export async function quickCreateProgram(values, topics = []) {
  const program = await saveProgram(values)
  if (!program.ok) return { ...program, coursesCreated: 0 }

  const topicIds = values.topicIds ?? []
  let created = 0

  for (const topicId of topicIds) {
    const topic = topics.find((candidate) => String(candidate.value) === String(topicId))
    /* ONE COURSE PER TOPIC, NAMED AFTER IT — as the legacy's CreateQuickProgram does. Every
       course used to take the PROGRAMME's names, so the second was refused as a duplicate
       name and the wizard stopped with half a programme made. The question count is capped
       at what the topic has, as the legacy caps it, for the same reason. */
    const available = topic?.examQuestions
    const course = await saveCourse({
      ...values,
      id: ENTITY_ID.NEW,
      programId: program.id,
      arabicName: topic?.arabicName || values.arabicName,
      englishName: topic?.englishName || values.englishName,
      arabicDescription: topic?.arabicName || values.arabicDescription,
      englishDescription: topic?.englishName || values.englishDescription,
      topicIds: [topicId],
      hasExam: true,
      questionsCount: available > 0 ? Math.min(Number(values.questionsCount), available) : values.questionsCount,
    })

    if (!course.ok) {
      return { ...course, id: program.id, coursesCreated: created }
    }
    created += 1
  }

  return { ...program, coursesCreated: created }
}

/** One programme, from the page payload that selects it. */
export async function getProgram(id, signal) {
  const page = await getProgramsPage(id, signal)
  return page.programs.find((program) => String(program.id) === String(id)) ?? null
}

/**
 * THIS SLICE ANSWERS IN ENGLISH, ALWAYS.
 *
 * Not one of its twenty-two handlers takes an `IStringLocalizer`; every message is a
 * hardcoded English literal — `"Operation completed successfully"`, `"Program not found"`
 * — so an Arabic panel showed English after every save. W6 says a server message is
 * displayed as received, and that is still the rule for the slices that localize; here
 * there is nothing to display.
 *
 * So the ten sentences this slice can produce are mapped back to the screen's own words,
 * and anything unrecognised passes through untouched — a message the server DOES localize
 * one day will simply start arriving in the right language and be shown as it is.
 *
 * Matched on the exact literal, lower-cased and with the trailing punctuation the handlers
 * are inconsistent about ("Error processing current request!" appears with and without a
 * semicolon). See `API_FINDINGS.md`.
 */
const SERVER_MESSAGES = {
  'operation completed successfully': () => S.messages.saved,
  'program not found': () => S.messages.programNotFound,
  'original program not found': () => S.messages.programNotFound,
  'course not found in program': () => S.messages.courseNotFound,
  'attachment not found': () => S.messages.attachmentNotFound,
  'program names already exist': () => S.messages.nameTaken,
  'input not valid': () => S.messages.invalidInput,
  'error processing current request': () => S.messages.requestFailed,
}

/** @returns {string} the localized equivalent, or the server's own words. */
function localizeMessage(message) {
  const key = String(message ?? '')
    .trim()
    .replace(/[!.;،]+$/, '')
    .toLowerCase()

  return SERVER_MESSAGES[key]?.() ?? message ?? ''
}

/** `{ ok, message, id }` — `ProgramOperationResponse` for every action here. */
async function act(path, body) {
  try {
    const data = await api.post(`/ProgramsManagement/${path}`, body)
    return {
      ok: data?.Result !== false,
      message: localizeMessage(data?.Message),
      id: data?.Id ?? null,
      unknown: data?.Unknown ?? [],
    }
  } catch (error) {
    if (error instanceof ApiError) {
      return { ok: false, message: localizeMessage(error.message), id: null, unknown: [] }
    }
    throw error
  }
}

/**
 * The longest basics the server stores — `ProgramBasicsInput.NameMaxLength` and
 * `DescriptionMaxLength`. The forms check them on the step they are typed on; otherwise an
 * over-long name passes "Next" and is only refused by the second step's Save.
 */
export const PROGRAM_BASICS_MAX = {
  arabicName: 300,
  englishName: 300,
  arabicDescription: 1000,
  englishDescription: 1000,
}

/**
 * One form, three endpoints.
 *
 * They run in order and stop at the first refusal, so a failure part-way leaves the earlier
 * parts saved — there is no transaction across them and no endpoint that takes the whole
 * programme. The caller is told which step failed through the message the server gave.
 */
/**
 * @param {object} values
 * @param {object} [parts] which legs to run. NOTHING is written that the caller did not
 *        ask for: the basics form seeds itself from the whole programme, so sniffing
 *        `values` for a `groupIds` or a `certificateId` made editing a NAME re-post the
 *        group assignment and the certificate settings as a side effect.
 */
export async function saveProgram(values, { groups = false, certificate = false } = {}) {
  /* Asked for a certificate and given no template: a refusal, before anything is written —
     not the basics' success reported as the certificate's. */
  if (certificate && !(Number(values.certificateId) > 0)) {
    return { ok: false, message: S.detail.certificatePanel.certificateRequired, id: values.id ?? null, unknown: [] }
  }

  const basics = await act('SaveProgramBasicInfos', {
    /* MINUS ONE MEANS "CREATE". `SaveProgramBasicInfoHandler` decides with
       `bool isNew = request.Id == -1` and nothing else — a zero is not new, it is an id,
       so it fell through to the update branch, looked for programme 0, found none and
       answered "Program not found". Every attempt to create a programme failed that way,
       from this form and from the quick wizard, which reports it as the first of its three
       steps refusing. `saveCourse` already sends -1; this was the one place that did not. */
    Id: Number(values.id) > 0 ? Number(values.id) : ENTITY_ID.NEW,
    /* The EDIT names, not the display ones — `values.name` is whichever language was
       showing, and writing it into `ArabicName` would overwrite the Arabic name with the
       English one. The form supplies these four directly. */
    ArabicName: values.arabicName ?? '',
    ArabicDescription: values.arabicDescription ?? '',
    EnglishName: values.englishName ?? '',
    EnglishDescription: values.englishDescription ?? '',
    /* Strings, not booleans — the record declares them as such. */
    PastCoursesView: String(values.pastCoursesView ?? ''),
    FutureCoursesView: String(values.futureCoursesView ?? ''),
    AllowAddingCoursesAfterExamination: Boolean(values.allowAddingCoursesAfterExamination),
  })

  if (!basics.ok) return basics

  // A create has no id until the first call answers with one.
  const id = Number(values.id ?? basics.id ?? 0)

  if (groups && values.groupIds) {
    const groups = await act('SaveProgramGroups', {
      Id: id,
      GroupIds: values.groupIds.map(Number),
      AssignToNewIntegratedGroups: Boolean(values.assignToNewIntegratedGroups),
    })
    if (!groups.ok) return groups
  }

  if (certificate && values.certificateId > 0) {
    const certificate = await act('SaveProgramCertificate', {
      Id: id,
      CertificateId: Number(values.certificateId),
      CertificateOptions: toCertificateOptions(values.certificate ?? {}),
    })
    if (!certificate.ok) return certificate
  }

  return { ...basics, id }
}

/**
 * `CertificateOptionsDto`.
 *
 * `CertificatePrintLanguage` IS AN INT — 1 Arabic, 2 English, 3 the reader's own UI language
 * (`GetCertificatePrintLanguage` in DownloadCertificatesHandler treats null and 3 alike). It
 * was sent as a string, which the DTO cannot bind. The three validity fields were left out
 * altogether, so a programme could never be given a certificate that expires.
 */
function toCertificateOptions(options) {
  return {
    CertificatePrintLanguage: Number(options.printLanguage) || CERTIFICATE_PRINT_LANGUAGE.USER_INTERFACE,
    CertificateValidityType: options.validityType != null ? Number(options.validityType) : null,
    CertificateValidity: options.validity != null ? Number(options.validity) : null,
    CertificateValidityVisibility: Number(
      options.validityVisibility ?? CERTIFICATE_VALIDITY_VISIBILITY.ADMINS_AND_USERS,
    ),
    EnableCertificateUserPrint: Boolean(options.userPrint),
    EnableCertificateGrade: Boolean(options.grade),
    CertificateDateFormat: Number(options.dateFormat ?? 0),
    CertificateDateConfig: Number(options.dateConfig ?? 0),
    EnableCertificatePersonalEmailSend: Boolean(options.personalEmail),
    EnableCertificateEmailSend: Boolean(options.emailSend),
    EnableCertificateDepartmentName: Boolean(options.departmentName),
    GradeA: Number(options.gradeA ?? 0),
    GradeB: Number(options.gradeB ?? 0),
    GradeC: Number(options.gradeC ?? 0),
  }
}

export const deleteProgram = (id) => act('DeleteProgram', { Id: Number(id) })

export const cloneProgram = ({ id, name, nameEn }) =>
  act('CloneProgram', { ProgramId: Number(id), ArabicName: name ?? '', EnglishName: nameEn ?? '' })

/* Each name gets the suffix in ITS OWN language. Both were taken from the panel's strings,
   so a copy made from the Arabic panel was called "Security (نسخة)" in English. */
const COPY_SUFFIX = { arabic: 'نسخة', english: 'Copy' }
const MAX_COPIES = 100

/**
 * A copy of a programme under the first free pair of names — "(نسخة)" / "(Copy)", then
 * "(نسخة 2)" / "(Copy 2)" and so on. The same two names were proposed every time, so the
 * second copy of any programme was refused as a duplicate with no way to name it.
 */
export async function cloneProgramAsCopy(program) {
  const { rows } = await listPrograms()
  const taken = new Set(rows.flatMap((row) => [row.arabicName.trim(), row.englishName.trim()]))
  const named = (n) => {
    const tag = (word) => (n === 1 ? `(${word})` : `(${word} ${n})`)
    return {
      id: program.id,
      name: `${program.arabicName.trim()} ${tag(COPY_SUFFIX.arabic)}`,
      nameEn: `${program.englishName.trim()} ${tag(COPY_SUFFIX.english)}`,
    }
  }
  for (let n = 1; n < MAX_COPIES; n += 1) {
    const candidate = named(n)
    if (!taken.has(candidate.name) && !taken.has(candidate.nameEn)) return cloneProgram(candidate)
  }
  return cloneProgram(named(MAX_COPIES))
}

export const setProgramActive = (programId, isActive) =>
  act('SetActivationStatus', { ProgramId: Number(programId), IsActive: Boolean(isActive) })

/**
 * The running order of the programme list.
 *
 * `ProgramOrderingInfo` is a STRING, not a list — the legacy drag handles built one and the
 * command still takes it that way. `id:order` pairs, comma separated.
 */
/**
 * The running order of the ACTIVE programmes, for the user's courses page.
 *
 * A JSON ARRAY OF IDS, and the format is not a detail: both readers — the admin page and
 * the user's courses page — test `StartsWith("[")` before parsing and fall back to
 * creation order for anything else. This was posting `"83:1,84:2"`, a positional string
 * nothing on the server has ever parsed, so every reorder saved successfully and changed
 * nothing anywhere. The legacy writes `JSON.stringify(ids)` (Views/ProgramsManagement/
 * TableJs.cshtml) and the sentinel `"creation"` to mean "no manual order".
 */
export const saveProgramsOrder = (orderedIds) =>
  act('SaveProgramsOrder', {
    ProgramOrderingInfo: orderedIds.length ? JSON.stringify(orderedIds.map(Number)) : 'creation',
  })

/** The courses of one programme, with their categories and dates. */
export async function listProgramCourses(programId, signal) {
  try {
    const rows =
      (await api.post(
        '/ProgramsManagement/GetProgramCoursesDetails',
        { Id: Number(programId) },
        { signal },
      )) ?? []

    /* THIS ONE ANSWERS `{ Status, Data }`, not a bare array and not `Courses` — see the
       handler. Reading the wrong key returned an empty list silently, which showed up as an
       edit form that opened blank. */
    return (Array.isArray(rows) ? rows : (rows.Data ?? rows.Courses ?? [])).map(toCourseRow)
  } catch (error) {
    if (error instanceof ApiError) return []
    throw error
  }
}

function toCourseRow(dto) {
  return {
    id: dto.Id,
    name: label(dto.NameArabic, dto.NameEnglish),
    nameEn: dto.NameEnglish ?? '',
    description: label(dto.DescriptionArabic, dto.DescriptionEnglish),
    /* EDIT fields beside the display ones, for the same reason `toProgramRow` carries both:
       `name` is whichever language is showing, so seeding an edit form from it would write
       the English name into the Arabic column. */
    arabicName: dto.NameArabic ?? '',
    englishName: dto.NameEnglish ?? '',
    arabicDescription: dto.DescriptionArabic ?? '',
    englishDescription: dto.DescriptionEnglish ?? '',
    createdOn: dto.CreatedOn ?? '',
    startDate: dto.StartDate ?? '',
    endDate: dto.EndDate ?? '',
    levelId: dto.LevelId ?? null,
    quizEnable: Boolean(dto.QuizEnable),
    hasExam: Boolean(dto.HasExam),
    contentType: dto.ContentType ?? 0,
    hasScormPackage: Boolean(dto.HasScormPackage),
    hasScormArabicPackage: Boolean(dto.HasScormArabicPackage),
    hasScormEnglishPackage: Boolean(dto.HasScormEnglishPackage),
    hasScormLegacyPackage: Boolean(dto.HasScormLegacyPackage),
    scormVersion: dto.ScormVersion ?? null,
    banner: dto.Banner ?? '',
    /* Ordered, not a set: this is the order the topics run in. */
    topicIds: (dto.Categories ?? [])
      .slice()
      .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
      .map((category) => category.Id),
  }
}

/**
 * One course, in the shape its form edits.
 *
 * TWO CALLS, and the second is the whole reason a course could not be edited before.
 * `SaveCourse` REWRITES on update: it removes the course's topics and re-adds whatever the
 * request carries (`CourseCategories.RemoveRange`), and it overwrites six exam fields from
 * `ExamDetails`. `GetProgramCoursesDetails` returns the topics but nothing about the exam,
 * so a form seeded from it alone would post its own defaults over the pass mark, the
 * attempt count and the three switches — silently.
 *
 * `GetCourseExamId` returns exactly those six, which is why the legacy calls it when it
 * opens a course (`setExamSettings` in CourseWizard.js). With both, an edit round-trips
 * without loss. The exam's QUESTION RULES are not in either payload and do not need to be:
 * the update path leaves them alone, touching only the six scalars.
 */
export async function getCourseForEdit(programId, courseId, signal) {
  const [courses, exam] = await Promise.all([
    listProgramCourses(programId, signal),
    getCourseExamId(courseId),
  ])

  const course = courses.find((row) => String(row.id) === String(courseId))
  if (!course) return null

  return {
    ...course,
    /* `level` is what the form field is called; `contentType` is a string there, and
       `hasExam` a string too — a spread of the row's boolean would make `hasExam === 'true'`
       false, hide the exam step, and post the exam away on the next save. */
    level: course.levelId != null ? String(course.levelId) : '1',
    contentType: Number(course.contentType) === 1 ? 'SCORM' : 'MP4',
    hasExam: course.hasExam ? 'true' : 'false',
    /* The list endpoint formats its dates under the request culture, so in Arabic these
       arrive as UmAlQura — `1448-03-04`. See `hijriToIso`. */
    startDate: hijriToIso(course.startDate),
    endDate: hijriToIso(course.endDate),
    examId: exam.examId,
    passLimit: exam.exam?.PassLimit ?? 50,
    examTries: exam.exam?.RetakeCounter ?? 3,
    forceWatchContent: Boolean(exam.exam?.ForceWatchContent),
    hideAnswers: Boolean(exam.exam?.HideAnswers),
    preventIfSuccess: Boolean(exam.exam?.PreventIfSucceeded),
    rewatchPercentage: exam.exam?.RewatchOnFailPercentage ?? '',
  }
}

/** Every course in the product, for the pickers. */
export async function listCourses(signal) {
  const rows = (await api.post('/ProgramsManagement/GetCourses', undefined, { signal })) ?? []
  return rows.map(toCourseRow)
}

/**
 * A course inside a programme.
 *
 * `Id` defaults to -1 server-side for "new", not 0 — the same negative-means-new convention
 * the categories wizard uses for its chapters.
 */
export async function saveCourse(values) {
  /* THE PICKED FILE HAS TO BE TURNED INTO BASE64, and nothing was doing it. The form stores a
     `File` under `values.banner` (`<FileField onChange={set('banner')} />`), while this builder
     read `values.bannerBase64` — a key with one consumer and no producer anywhere in the app.
     So `BannerBase64` posted as null on every save and `SaveBannerAsync` never ran: picking a
     thumbnail appeared to work, reported success, and wrote neither a file nor a column.

     `fileToBase64` exists for exactly this and says so; the attachments drawer and the policies
     service both call it the same way, from the service rather than the form. */
  const bannerBase64 = values.bannerBase64 ?? (values.banner instanceof Blob ? await fileToBase64(values.banner) : '')

  return act('SaveCourse', {
    Id: Number(values.id ?? ENTITY_ID.NEW),
    ProgramId: Number(values.programId ?? 0),
    /* `SCORM` on the form, a number on the wire. */
    ContentType: contentTypeOf(values.contentType),
    /* `hasExam` IS A STRING here — the radio's value. `Boolean('false')` is true, so the
       plain cast posted an exam for every course whether or not one was asked for. */
    HasExam: wantsExam(values),
    /* The form's own field names, which are the API's — see `saveProgram`. */
    ArabicName: values.arabicName ?? '',
    EnglishName: values.englishName ?? '',
    ArabicDescription: values.arabicDescription ?? '',
    EnglishDescription: values.englishDescription ?? '',
    QuizEnable: Boolean(values.quizEnable),
    Level: Number(values.level ?? 0),
    StartDate: values.startDate || null,
    EndDate: values.endDate || null,
    /* A comma-separated string of topic ids IN ORDER, not an array. */
    /* A JSON ARRAY IN A STRING, not a comma-joined list. `SaveCourseHandler` reads this
       field with `JsonConvert.DeserializeObject<List<int>>(request.Categories)`, so one
       topic arrived as "43" and threw "Error converting value 43 to type List<Int32>" —
       a 500, and no course. Two topics arrived as "43,44", which is not JSON at all. */
    Categories: JSON.stringify((values.topicIds ?? []).map(Number)),
    // Base64 in the JSON, not a file part. See the note at the top.
    BannerBase64: bannerBase64 || null,
    ScormPackageArabicBase64: contentTypeOf(values.contentType) === COURSE_CONTENT_TYPE.SCORM ? (await fileToBase64(values.scormArabic)) || null : null,
    ScormPackageEnglishBase64: contentTypeOf(values.contentType) === COURSE_CONTENT_TYPE.SCORM ? (await fileToBase64(values.scormEnglish)) || null : null,
    ScormExistingLanguage: values.hasScormLegacyPackage ? values.scormExistingLanguage || null : null,
    ExamDetails: wantsExam(values) ? toExamDetails(values) : null,
  })
}

/** The radio speaks in `'true'`/`'false'`; a checkbox elsewhere may speak in booleans. */
const wantsExam = (values) => values.hasExam !== 'false' && Boolean(values.hasExam)

/** The form offers `SCORM` or video; the command takes a number. */
const contentTypeOf = (value) =>
  typeof value === 'number' ? value : value === 'SCORM' ? 1 : 0

/**
 * The exam sits on the same form as its course, in flat fields, and goes over nested.
 */
function toExamDetails(values) {
  return {
    ExamId: Number(values.examId ?? 0),
    PassLimit: Number(values.passLimit ?? 0),
    ExamTries: Number(values.examTries ?? 0),
    ExamQuestions: Number(values.questionsCount ?? 0),
    ExamForcingRewatchOnFailPercentage:
      values.rewatchPercentage === '' || values.rewatchPercentage == null
        ? null
        : Number(values.rewatchPercentage),
    ForceWatchContent: Boolean(values.forceWatchContent),
    HideAnswers: Boolean(values.hideAnswers),
    PreventIfSuccess: Boolean(values.preventIfSuccess),
  }
}

export const deleteCourse = (programId, courseId) =>
  act('DeleteCourse', { ProgramId: Number(programId), Id: Number(courseId) })

/**
 * Whether a course already has an exam — the legacy's `GetourseExamId`, which decides
 * whether the exam form opens on an existing exam or a blank one.
 *
 * NOT through `act`. That helper normalises every reply to `{ ok, message, id, unknown }`
 * for the save-and-report actions, which drops `ExamId` on the floor — so this read came
 * back with the one field it exists to return missing, and every course looked as though it
 * had no exam. It had never been called, so nothing had shown it up.
 *
 * @returns {Promise<{examId: number, exam: object|null}>} `examId` is 0 when there is none.
 */
export async function getCourseExamId(courseId) {
  try {
    const data = await api.post('/ProgramsManagement/GetCourseExamId', {
      CourseId: Number(courseId),
    })
    return { examId: Number(data?.ExamId ?? 0), exam: data?.Exam ?? null }
  } catch {
    return { examId: 0, exam: null }
  }
}

/* ------------------------------------------------------------------ *
 * Course attachments
 * ------------------------------------------------------------------ */

export async function listCourseAttachments(courseId, signal) {
  try {
    const rows =
      (await api.post(
        '/ProgramsManagement/GetCourseAttachments',
        { CourseId: Number(courseId) },
        { signal },
      )) ?? []

    return rows.map((dto) => ({
      id: dto.Id,
      courseId: dto.CourseId,
      name: label(dto.ArabicName, dto.EnglishName),
      nameEn: dto.EnglishName ?? '',
      /* The DOWNLOAD name, which is not the display name: the server stores the file under
         one and shows the other. */
      fileAr: dto.ArabicNameDL ?? '',
      fileEn: dto.EnglishNameDL ?? '',
    }))
  } catch (error) {
    if (error instanceof ApiError) return []
    throw error
  }
}

/** @param {string} [fileArabic] base64, with its type in `fileArabicType`. */
export const addCourseAttachment = (values) =>
  act('AddAttachment', {
    CourseId: Number(values.courseId),
    ArabicName: values.name ?? '',
    EnglishName: values.nameEn ?? '',
    FileArabic: values.fileArabic ?? '',
    FileArabicType: values.fileArabicType ?? '',
    FileEnglish: values.fileEnglish ?? '',
    FileEnglishType: values.fileEnglishType ?? '',
  })

export const updateCourseAttachment = (values) =>
  act('UpdateAttachment', {
    Id: Number(values.id),
    CourseId: Number(values.courseId),
    ArabicName: values.name ?? '',
    EnglishName: values.nameEn ?? '',
    FileArabic: values.fileArabic ?? '',
    FileArabicType: values.fileArabicType ?? '',
    FileEnglish: values.fileEnglish ?? '',
    FileEnglishType: values.fileEnglishType ?? '',
  })

export const removeCourseAttachment = (attachmentId) =>
  act('RemoveCourseAttachments', { AttachmentId: Number(attachmentId) })

/* ------------------------------------------------------------------ *
 * Per-user exceptions — extra time, extra exam attempts
 * ------------------------------------------------------------------ */

/** What `GetExceptionsUsersHandler` can order by, by the names the drawer's table sends. */
export const EXCEPTION_SORTABLE = ['user', 'department', 'tries', 'until']

export async function listExceptionUsers({ programId, courseId, page = 1, pageSize = 10, search = '', sort = null, signal } = {}) {
  try {
    const response = await api.post(
      '/ProgramsManagement/GetExceptionsUsers',
      {
        ProgramId: Number(programId),
        CourseId: Number(courseId),
        Draw: 1,
        Start: (Math.max(page, 1) - 1) * pageSize,
        Length: pageSize,
        SearchValue: search,
        SortBy: sort?.key ?? null,
        SortDesc: sort?.dir === 'desc',
      },
      { signal },
    )

    const rows = response?.Data ?? response?.data ?? []
    return { rows, total: response?.RecordsFiltered ?? response?.recordsFiltered ?? rows.length }
  } catch (error) {
    if (error instanceof ApiError) return { rows: [], total: 0 }
    throw error
  }
}

/** @param {string[]} users usernames. */
export const setUsersExceptions = ({ users, programId, courseId, type, canAttendUntil, examTries }) =>
  act('SetUsersFromExceptions', {
    Users: users,
    ProgramId: Number(programId),
    CourseId: Number(courseId),
    Type: Number(type ?? COURSE_EXCEPTION_TYPE.NONE),
    CanAttendUntil: canAttendUntil || null,
    NumberOfExamTries: examTries == null ? null : Number(examTries),
  })

export const removeUsersExceptions = ({ users, programId, courseId }) =>
  act('RemoveUsersFromExceptions', {
    Users: users,
    ProgramId: Number(programId),
    CourseId: Number(courseId),
  })

export const addCourseNotification = (courseId, values) =>
  act('AddNotification', { CourseId: Number(courseId), ...values })
